// Holding pallets on the Sales Order page's "Prepare shipment" (jobq-api docs/holding-pallets-design.md section 4,
// Mike 2026-10-27: every part goes on a holding pallet at the laser; final pallets are assigned at shipment prep).
// A holding pallet IS a laser task's WIP pallet (`planned_pallets.wip_task_id`, palletRules.isTaskWipPallet). At
// shipment prep each one is either shipped as it is (POST .../promote: it becomes the shipping pallet) or packed into
// new pallets / boxes (the Staging queue's assign rows, manual-assign). Pure: no requests.
//
// load_plan fields read (an older API may lack the newer ones: each falls back):
//   pallet: wip_task_id, wip_station (live next station, "Packing" when no in-house step is left), wip_next_station,
//           wip_master_job_num, short_id, status, beacon_id, beacon_light, can_promote, promote_blocker,
//           promote_override_allowed, boxes[].zones[]
//   zone:   part_id, product_id, description, actual_qty (cut, on the pallet), reserved_qty (claimed, still cutting);
//           after the join an ASSEMBLY stack: assembly_id, assembly_name, primary_part_quantity (counts are in
//           primary-part space, shown as assemblies, as SOPlannedBoxCard does)
//   plan.staged[].on_wip [{pallet_id, qty, reserved, ...}]: the same counts per (part, holding pallet)
import { isTaskWipPallet, taskWipStation } from './palletRules';
import { assemblyCountFromPrimarySpace } from '../../utils/assemblyMath';

/** A holding pallet still in play: a task WIP pallet that is Building (an emptied one ends Consolidated, a promoted
 *  one loses its wip_task_id and is an ordinary shipping pallet). */
export function isHoldingPallet(pallet) {
  return isTaskWipPallet(pallet) && (pallet.status || 'Building') === 'Building';
}

export function holdingPallets(plan) {
  return (plan?.pallets || []).filter(isHoldingPallet);
}

/** The Staging queue becomes Prepare shipment when the plan has a holding pallet in play. */
export function planHasHoldingPallets(plan) {
  return holdingPallets(plan).length > 0;
}

const isPacking = (station) => String(station || '').trim().toLowerCase() === 'packing';

/** Where the holding pallet stands: {station, ready, label}. ready = no in-house step left ("Ready to pack");
 *  otherwise the label reads as the pallet's own label and beacon: "STAGE FOR BRAKE". */
export function holdingStationState(pallet) {
  const station = taskWipStation(pallet);
  const ready = isPacking(station);
  return { station, ready, label: ready ? 'Ready to pack' : `STAGE FOR ${String(station).toUpperCase()}` };
}

/** The pieces on a holding pallet, one row per part (an assembly stack is its own row, keyed by part AND assembly), in
 *  the order they first appear: [{key, part_id, product_id, description, cut, cutting, assembly_id, assembly_name, ppq,
 *  isAssembly}]. cut = on the pallet now (zones' actual_qty), cutting = claimed and still to cut (reserved_qty), both in
 *  pieces (primary-part space for an assembly). The Staging queue's on_wip line for this pallet, when the API sends
 *  one, wins for that part's plain row (the same counts, as the API worked them out; on_wip carries no assembly). */
export function holdingPalletParts(pallet, plan) {
  const rows = new Map();
  const row = (partId, seed, assemblyId) => {
    const isAssembly = assemblyId != null && !!seed.assembly_name;
    const key = isAssembly ? `${partId}-a${assemblyId}` : String(partId);
    if (!rows.has(key)) {
      rows.set(key, {
        key, part_id: partId, product_id: seed.product_id || null, description: seed.description || '', cut: 0, cutting: 0,
        assembly_id: isAssembly ? assemblyId : null, assembly_name: isAssembly ? seed.assembly_name : null,
        ppq: isAssembly ? Math.max(1, parseInt(seed.primary_part_quantity, 10) || 1) : 1, isAssembly,
      });
    }
    return rows.get(key);
  };
  for (const box of pallet?.boxes || []) {
    for (const z of box.zones || []) {
      const r = row(z.part_id, z, z.assembly_id);
      r.cut += Math.max(0, Number(z.actual_qty) || 0);
      r.cutting += Math.max(0, Number(z.reserved_qty) || 0);
    }
  }
  for (const it of plan?.staged || []) {
    for (const w of it.on_wip || []) {
      if (w.pallet_id !== pallet?.pallet_id) continue;
      const r = row(it.part_id, it, null);
      if (!r.product_id) r.product_id = it.product_id || null;
      if (!r.description) r.description = it.description || '';
      r.cut = Math.max(0, Number(w.qty) || 0);
      r.cutting = Math.max(0, Number(w.reserved) || 0);
    }
  }
  return [...rows.values()];
}

/** A row's count in the unit the office packs and ships: whole assemblies for an assembly stack, else pieces. */
export function rowUnits(r, field = 'cut') {
  const n = Number(r?.[field]) || 0;
  return r?.isAssembly ? assemblyCountFromPrimarySpace(n, r.ppq) : n;
}

/** "19 cut, 33 still being cut" / "52 being cut" / "52 cut"; an assembly stack: "3 × HY-FLO STAND". Only cut pieces
 *  can be packed or shipped. */
export function piecesText(r = {}) {
  if (r.isAssembly) return `${rowUnits(r)} × ${r.assembly_name}`;
  const { cut = 0, cutting = 0 } = r;
  if (cutting > 0 && cut > 0) return `${cut} cut, ${cutting} still being cut`;
  if (cutting > 0) return `${cutting} being cut`;
  return `${cut} cut`;
}

/** "Ship this pallet as it is": {offered, enabled, why, override}. An API without the promote endpoint sends no
 *  `can_promote`: the button is not offered at all. Blocked (can_promote false), `why` is the API's promote_blocker:
 *  the button is disabled, unless the API says a manager may let it through (`promote_override_allowed`: an in-house
 *  step still owed). Then it stays enabled, `override` is true, the blocker shows as a warning, and the promote's 409
 *  goes through palletActions.postOrOverride's manager override. */
export function promoteState(pallet) {
  if (!pallet || !Object.prototype.hasOwnProperty.call(pallet, 'can_promote')) {
    return { offered: false, enabled: false, why: null, override: false };
  }
  if (pallet.can_promote === true) return { offered: true, enabled: true, why: null, override: false };
  const why = pallet.promote_blocker || 'JobQ cannot ship this pallet as it is yet.';
  if (pallet.promote_override_allowed === true) return { offered: true, enabled: true, why, override: true };
  return { offered: true, enabled: false, why, override: false };
}

/** The Staging queue rows that are on no holding pallet (old plans' rows, parts the laser did not put on one). */
export function stagedOffHolding(plan) {
  return (plan?.staged || []).filter((it) => !(it.on_wip || []).length);
}

/** What "Ship this pallet as it is" answers: "PLT-K2Q8 is a shipping pallet now (BCN-007 stays on it): seal it when
 *  it is ready to go." */
export function promotedText(res, fallbackName) {
  const name = res?.short_id || fallbackName;
  const beacon = res?.beacon_id ? ` (${res.beacon_id} stays on it)` : '';
  return `${name} is a shipping pallet now${beacon}: seal it when it is ready to go.`;
}
